import axios from "axios";
import { rootStore } from "@/mobx/store";
import { ENV } from "@/config/env";

// 토큰 갱신 시도 횟수를 추적하는 변수
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;

// 토큰 갱신 함수
export const refreshAccessToken = async () => {
  // 무한 루프 방지
  if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
    console.error("Max token refresh attempts reached");
    refreshAttempts = 0; // 리셋
    throw new Error("Max token refresh attempts reached");
  }

  refreshAttempts++;

  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    console.log("Sending token refresh request...");

    // API_URL이 이미 슬래시로 끝나므로 이중 슬래시 방지
    const refreshUrl = `${ENV.API_URL.replace(/\/$/, "")}/auth/refresh`;
    console.log("Refresh URL:", refreshUrl);

    // 토큰 갱신 요청에 타임아웃 설정
    const response = await axios.post(
      refreshUrl,
      { refreshToken },
      {
        timeout: 10000, // 10초 타임아웃
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        withCredentials: true, // 쿠키 포함
      }
    );

    console.log("Token refresh response:", response.data);

    // API 응답 구조 확인 및 처리
    let accessToken = null;
    let newRefreshToken = null;

    if (response.data.accessToken) {
      // 직접 accessToken이 있는 경우
      accessToken = response.data.accessToken;
      newRefreshToken = response.data.refreshToken;
    } else if (response.data.result_data?.accessToken) {
      // ApiResponse 구조인 경우
      accessToken = response.data.result_data.accessToken;
      newRefreshToken = response.data.result_data.refreshToken;
    } else if (response.data.result_data) {
      // result_data 자체가 토큰인 경우
      accessToken = response.data.result_data;
    }

    if (accessToken) {
      // MobX store의 setToken 메서드 사용 (이 메서드가 내부적으로 모든 처리를 함)
      rootStore.setToken(accessToken);

      // 새로운 refresh token이 있다면 저장
      if (newRefreshToken) {
        localStorage.setItem("refresh_token", newRefreshToken);
      }

      refreshAttempts = 0; // 성공 시 리셋
      return accessToken;
    } else {
      console.error("No access token found in response:", response.data);
      throw new Error("Token refresh failed - no access token in response");
    }
  } catch (error: any) {
    console.error("Token refresh failed:", error);
    console.error("Error details:", {
      message: error.message,
      code: error.code,
      response: error.response?.data,
      status: error.response?.status,
    });

    // 네트워크 오류인 경우 재시도 로직
    if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
      console.log(
        `Network error during token refresh, attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}`
      );

      if (refreshAttempts < MAX_REFRESH_ATTEMPTS) {
        // 잠시 대기 후 재시도
        const waitTime = 1000 * refreshAttempts;
        console.log(`Waiting ${waitTime}ms before retry...`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
        return refreshAccessToken();
      }
    }

    // 최대 시도 횟수 초과 또는 다른 오류
    refreshAttempts = 0; // 리셋
    throw error;
  }
};

// 토큰이 유효한지 확인하는 함수
export const isTokenValid = (token: string): boolean => {
  if (!token) return false;

  try {
    // JWT 토큰의 payload 부분을 디코드
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;

    // 토큰 만료 시간 확인
    return payload.exp > currentTime;
  } catch (error) {
    console.error("Token validation failed:", error);
    return false;
  }
};

// 현재 토큰 가져오기
export const getCurrentToken = (): string | null => {
  return rootStore.token;
};

// 토큰이 곧 만료될 예정인지 확인 (5분 전)
export const isTokenExpiringSoon = (token: string): boolean => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Date.now() / 1000;
    const fiveMinutes = 5 * 60; // 5분

    return payload.exp - currentTime < fiveMinutes;
  } catch (error) {
    console.error("Token expiration check failed:", error);
    return true;
  }
};

// 토큰 갱신 시도 횟수 리셋 함수
export const resetRefreshAttempts = () => {
  refreshAttempts = 0;
};
